import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { GalleryMedia } from '../../types';
import { ToggleSwitch } from '../../../Common/ToggleSwitch/ToggleSwitch';
import { OptimizedImage } from '../../../OptimizedImage';
import ProgressiveMainImage from './ProgressiveMainImage';
import styles from './DesktopView.module.css';

interface DesktopViewProps {
  image: GalleryMedia;
  images: GalleryMedia[];
  isVideo: boolean;
  isSidebarOpen: boolean;
  isZoomEnabled: boolean;
  setIsZoomEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Video
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  buffered: number;
  showBigPlay: boolean;
  togglePlay: (e?: React.MouseEvent) => void;
  handleVideoTimeUpdate: () => void;
  handleVideoLoadedMetadata: () => void;
  handleVideoProgress: () => void;
  handleVideoPlay: () => void;
  handleVideoPause: () => void;
  handleProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  toggleMute: (e: React.MouseEvent) => void;
  toggleFullscreen: (e: React.MouseEvent) => void;
  formatTime: (time: number) => string;
  // Zoom
  imageWrapperRef: React.RefObject<HTMLDivElement | null>;
  handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: () => void;
  handleImageClick: (e: React.MouseEvent<HTMLImageElement>) => void;
  // Thumbnails
  thumbnailsRef: React.RefObject<HTMLDivElement | null>;
  onThumbnailClick: (img: GalleryMedia) => void;
  onContentClick: (e: React.MouseEvent) => void;
}

export const DesktopView: React.FC<DesktopViewProps> = ({
  image,
  images,
  isVideo,
  isSidebarOpen,
  isZoomEnabled,
  setIsZoomEnabled,
  setIsSidebarOpen,
  videoRef,
  isPlaying,
  currentTime,
  duration,
  volume,
  isMuted,
  buffered,
  showBigPlay,
  togglePlay,
  handleVideoTimeUpdate,
  handleVideoLoadedMetadata,
  handleVideoProgress,
  handleVideoPlay,
  handleVideoPause,
  handleProgressClick,
  handleVolumeChange,
  toggleMute,
  toggleFullscreen,
  formatTime,
  imageWrapperRef,
  handleMouseMove,
  handleMouseLeave,
  handleImageClick,
  thumbnailsRef,
  onThumbnailClick,
  onContentClick,
}) => {
  const { t } = useTranslation(['image', 'common']);

  return (
    <div className={clsx(styles.desktopView, !isSidebarOpen && styles.fullWidth)}>
      <div className={styles.imageContainer}>
        <div
          ref={imageWrapperRef as React.RefObject<HTMLDivElement>}
          className={clsx(styles.imageWrapper, isVideo && styles.videoWrapper, !isVideo && isZoomEnabled && styles.zoomActive)}
          onClick={onContentClick}
          onMouseMove={isVideo ? undefined : handleMouseMove}
          onMouseLeave={isVideo ? undefined : handleMouseLeave}
        >
          {isVideo ? (
            <>
              <video
                ref={videoRef as React.RefObject<HTMLVideoElement>}
                src={image.url}
                poster={image.thumbnailUrl}
                className={styles.mainVideo}
                playsInline
                loop
                muted={isMuted}
                onClick={togglePlay}
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={handleVideoTimeUpdate}
                onLoadedMetadata={handleVideoLoadedMetadata}
                onProgress={handleVideoProgress}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
              />

              <button
                className={clsx(styles.videoBigPlayButton, !showBigPlay && styles.hidden)}
                onClick={togglePlay}
              >
                <i className="fa-solid fa-play" />
              </button>

              <div className={styles.videoControls} onClick={(e) => e.stopPropagation()}>
                <div className={styles.videoProgress} onClick={handleProgressClick}>
                  <div className={styles.videoProgressBuffer} style={{ width: `${buffered}%` }} />
                  <div className={styles.videoProgressFill} style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                </div>

                <div className={styles.videoControlsRow}>
                  <div className={styles.videoControlsLeft}>
                    <button className={clsx(styles.videoControlBtn, styles.playPause)} onClick={togglePlay}>
                      <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'}`} />
                    </button>

                    <div className={styles.volumeControl}>
                      <button className={styles.videoControlBtn} onClick={toggleMute}>
                        <i className={`fa-solid ${isMuted || volume === 0 ? 'fa-volume-xmark' : volume < 0.5 ? 'fa-volume-low' : 'fa-volume-high'}`} />
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className={styles.volumeSlider}
                      />
                    </div>

                    <span className={styles.videoTime}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>

                  <div className={styles.videoControlsRight}>
                    <button className={styles.videoControlBtn} onClick={toggleFullscreen}>
                      <i className="fa-solid fa-expand" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <ProgressiveMainImage
              src={image.url}
              thumbnailUrl={image.thumbnailUrl}
              alt={image.title}
              isZoomEnabled={isZoomEnabled}
              onClick={handleImageClick}
            />
          )}

          <div className={styles.imageControls} onClick={(e) => e.stopPropagation()}>
            {!isVideo && (
              <div className={styles.controlWrapper}>
                <div
                  className={styles.zoomToggle}
                  onClick={() => setIsZoomEnabled(!isZoomEnabled)}
                  role="button"
                  tabIndex={0}
                >
                  <span className={clsx(styles.zoomLabel, isZoomEnabled && styles.active)}>Zoom</span>
                  <div className={styles.zoomSwitchWrap} onClick={(e) => e.stopPropagation()}>
                    <ToggleSwitch value={isZoomEnabled} onChange={setIsZoomEnabled} />
                  </div>
                </div>
                <div className={styles.tooltipBelow}>
                  <span>{isZoomEnabled ? t('gallery.zoomOff') : t('gallery.zoomOn')}</span>
                  <div className={styles.tooltipArrowUp} />
                </div>
              </div>
            )}

            <div className={styles.controlWrapper}>
              <button className={styles.expandToggle} onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                <i className={`fa-solid ${isSidebarOpen ? 'fa-expand' : 'fa-list-ul'}`} />
              </button>
              <div className={clsx(styles.tooltipBelow, styles.tooltipRight)}>
                <span>{isSidebarOpen ? t('gallery.fullscreen') : t('gallery.showDetails')}</span>
                <div className={styles.tooltipArrowUp} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.thumbnailStrip}>
        <div
          ref={thumbnailsRef as React.RefObject<HTMLDivElement>}
          className={clsx(styles.thumbnailsContainer, 'hideScrollbar')}
          onClick={onContentClick}
        >
          {images.map((img) => (
            <button
              key={img.id}
              className={clsx(
                styles.thumbnailButton,
                image.id === img.id && styles.active,
                img.type === 'video' && styles.videoThumbnail
              )}
              onClick={() => onThumbnailClick(img)}
            >
              {img.type === 'video' && !img.thumbnailUrl ? (
                <video
                  src={`${img.url}#t=0.001`}
                  className={styles.thumbnailImage}
                  muted
                  playsInline
                  preload="metadata"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <OptimizedImage
                  src={img.thumbnailUrl || img.url}
                  alt=""
                  className={styles.thumbnailImage}
                  sizes="48px"
                  width={128}
                />
              )}
              {img.type === 'video' && (
                <div className={styles.thumbnailPlayIcon}>
                  <i className="fa-solid fa-play" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
